/**
 * Email settings (Resend) — daily brief + automatic stock/IFRA alerts.
 * Rendered inside Settings → Email tab.
 */
import { useEffect, useState } from "react";
import { Card, SectionHeader, Button, TextInput } from "./lib";
import { Field } from "./lib.overlay";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

type Settings = {
  dailyBriefEnabled?: boolean; briefTo?: string; briefHourUTC?: number;
  alertsEnabled?: boolean; alertsTo?: string; lastBriefDate?: string;
};

async function req(path: string, opts: RequestInit = {}) {
  const r = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
  return j;
}

const Toggle = ({ on, onChange, testId }: { on: boolean; onChange: (v: boolean) => void; testId: string }) => (
  <button
    data-testid={testId}
    onClick={() => onChange(!on)}
    style={{
      width: 42, height: 22, borderRadius: 999, border: "1px solid var(--ph-border-strong)",
      background: on ? "var(--ph-gold, #B8965A)" : "var(--ph-surface-2, #e8e4da)",
      position: "relative", cursor: "pointer", transition: "background 0.2s",
    }}
  >
    <span style={{
      position: "absolute", top: 2, left: on ? 22 : 2, width: 16, height: 16,
      borderRadius: "50%", background: "#fff", transition: "left 0.2s",
      boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
    }} />
  </button>
);

export const EmailSettingsCard = () => {
  const [st, setSt] = useState<Settings>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => { req("/email/settings").then(setSt).catch(() => {}); }, []);

  const set = (k: keyof Settings, v: unknown) => setSt((c) => ({ ...c, [k]: v }));

  async function save() {
    setBusy("save"); setMsg(null);
    try {
      const saved = await req("/email/settings", {
        method: "PATCH",
        body: JSON.stringify({
          dailyBriefEnabled: !!st.dailyBriefEnabled, briefTo: st.briefTo || "",
          briefHourUTC: Number(st.briefHourUTC ?? 5),
          alertsEnabled: !!st.alertsEnabled, alertsTo: st.alertsTo || "",
        }),
      });
      setSt(saved);
      setMsg({ ok: true, text: "Saved — the scheduler checks every 30 minutes." });
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
    finally { setBusy(null); }
  }

  async function sendNow() {
    setBusy("brief"); setMsg(null);
    try {
      const r = await req("/email/daily-brief/send-now", { method: "POST", body: JSON.stringify({}) });
      setMsg({ ok: true, text: `Brief sent ✓ (id: ${r.emailId || "ok"})` });
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
    finally { setBusy(null); }
  }

  return (
    <Card testId="settings-email-card">
      <SectionHeader eyebrow="Resend integration" title="Email · Daily brief & alerts" />
      <div className="text-xs mb-4" style={{ color: "var(--ph-ink-muted)" }}>
        Sender: <b>onboarding@resend.dev</b> (test mode — delivers only to your Resend account email).
        Verify <b>orvan-perfumes.com</b> in Resend → Domains to send to anyone.
      </div>

      <Field label="Daily morning brief" hint="Sales, stock and alert summary once a day." inline>
        <Toggle on={!!st.dailyBriefEnabled} onChange={(v) => set("dailyBriefEnabled", v)} testId="email-brief-toggle" />
      </Field>
      <Field label="Brief recipient" inline>
        <div style={{ width: 300 }}>
          <TextInput value={st.briefTo || ""} onChange={(v) => set("briefTo", v)} testId="email-brief-to" />
        </div>
      </Field>
      <Field label="Send hour (UTC)" hint="e.g. 5 = 08:00 KSA / 07:00 Cairo" inline>
        <div style={{ width: 90 }}>
          <TextInput value={String(st.briefHourUTC ?? 5)} onChange={(v) => set("briefHourUTC", Number(v) || 0)} testId="email-brief-hour" />
        </div>
      </Field>

      <div className="my-4 border-t" style={{ borderColor: "var(--ph-border)" }} />

      <Field label="Automatic alerts" hint="Low-stock materials + expiring IFRA certificates (checked every 30 min, one email per alert per week)." inline>
        <Toggle on={!!st.alertsEnabled} onChange={(v) => set("alertsEnabled", v)} testId="email-alerts-toggle" />
      </Field>
      <Field label="Alerts recipient" inline>
        <div style={{ width: 300 }}>
          <TextInput value={st.alertsTo || ""} onChange={(v) => set("alertsTo", v)} testId="email-alerts-to" />
        </div>
      </Field>

      {msg && (
        <div data-testid="email-settings-msg" className="mt-3 p-3 text-sm" style={{
          background: msg.ok ? "var(--ph-success-tint)" : "var(--ph-danger-tint)",
          color: msg.ok ? "var(--ph-success)" : "var(--ph-danger)",
          borderRadius: "var(--ph-radius-sm)",
        }}>{msg.text}</div>
      )}

      <div className="pt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={sendNow} disabled={busy !== null} testId="email-brief-send-now">
          {busy === "brief" ? "Sending…" : "Send brief now"}
        </Button>
        <Button onClick={save} disabled={busy !== null} testId="email-settings-save">
          {busy === "save" ? "Saving…" : "Save"}
        </Button>
      </div>
    </Card>
  );
};
